import React, { useEffect, useRef } from "react";

/**
 * ParticleField - a lightweight canvas particle engine.
 * Renders soft glowing "dew / firefly" motes with depth layers,
 * gentle upward drift, sinusoidal sway, twinkle, and pointer parallax.
 * Original implementation tuned for the Bahra spa palette.
 */

// Palette: candle amber glow, warm cream, taupe smoke, and a rare ember (harmonized with the candle hero)
const COLORS = [
  [200, 154, 74],
  [246, 241, 232],
  [217, 183, 122],
  [185, 163, 143],
  [255, 236, 205],
  [107, 46, 42],
];

const rand = (a, b) => a + Math.random() * (b - a);

/** Create the initial particle set with randomized depth-scaled properties. */
const createParticles = (count) =>
  Array.from({ length: count }, () => {
    const depth = rand(0.25, 1); // 1 = closest
    return {
      x: Math.random(),
      y: Math.random(),
      depth,
      r: rand(1.2, 3.4) * depth,
      speed: rand(0.008, 0.024) * depth,
      swayAmp: rand(6, 22) * depth,
      swayFreq: rand(0.2, 0.6),
      phase: rand(0, Math.PI * 2),
      twinkleFreq: rand(0.3, 1.1),
      baseAlpha: rand(0.25, 0.7) * depth,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    };
  });

/** Draw a single particle as a soft radial glow for the given frame. */
const drawParticle = (ctx, p, frame) => {
  const { t, w, h, px, py } = frame;
  const sway = Math.sin(t * 0.001 * p.swayFreq + p.phase) * p.swayAmp;
  const x = p.x * w + sway + px * 18 * p.depth;
  const y = p.y * h + py * 12 * p.depth;
  const twinkle = 0.55 + 0.45 * Math.sin(t * 0.001 * p.twinkleFreq * 2 + p.phase);
  const alpha = p.baseAlpha * twinkle;
  const [r, g, b] = p.color;
  const rad = p.r * (1 + 0.25 * twinkle);

  const grd = ctx.createRadialGradient(x, y, 0, x, y, rad * 4);
  grd.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
  grd.addColorStop(0.4, `rgba(${r},${g},${b},${alpha * 0.35})`);
  grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(x, y, rad * 4, 0, Math.PI * 2);
  ctx.fill();
};

/** Advance a particle's drift; recycle it to the bottom once off-screen. */
const stepParticle = (p) => {
  p.y -= p.speed / 100;
  if (p.y < -0.05) {
    p.y = 1.05;
    p.x = Math.random();
  }
};

/** Fit the canvas backing store to its parent (device-pixel-ratio aware). */
const sizeCanvasToParent = (canvas, ctx) => {
  const rect = canvas.parentElement.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { w: rect.width, h: rect.height };
};

/** Create a pausable requestAnimationFrame loop around a draw callback. */
const createLoop = (drawFrame, reduced) => {
  let raf = null;
  let running = false;
  const tick = (t) => {
    if (!running) return;
    drawFrame(t);
    if (reduced) return; // single static render
    raf = requestAnimationFrame(tick);
  };
  return {
    start() {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    },
  };
};

/** Attach resize / pointer / visibility listeners; returns an unbind function. */
const wireEngineEvents = (canvas, { onResize, onPointer, onVisibility }) => {
  const ro = new ResizeObserver(onResize);
  ro.observe(canvas.parentElement);
  window.addEventListener("pointermove", onPointer, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    ro.disconnect();
    window.removeEventListener("pointermove", onPointer);
    document.removeEventListener("visibilitychange", onVisibility);
  };
};

/**
 * Boot the particle engine on a canvas. Owns sizing, the animation loop,
 * pointer parallax, and tab-visibility pausing. Returns a cleanup function.
 */
const startParticleEngine = (canvas, count) => {
  const ctx = canvas.getContext("2d");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const particles = createParticles(count);
  const pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
  let { w, h } = sizeCanvasToParent(canvas, ctx);

  const drawFrame = (t) => {
    ctx.clearRect(0, 0, w, h);
    pointer.x += (pointer.tx - pointer.x) * 0.04; // ease for silky parallax
    pointer.y += (pointer.ty - pointer.y) * 0.04;
    const frame = { t, w, h, px: (pointer.x - 0.5) * 2, py: (pointer.y - 0.5) * 2 };
    for (const p of particles) {
      drawParticle(ctx, p, frame);
      if (!reduced) stepParticle(p);
    }
  };

  const loop = createLoop(drawFrame, reduced);
  const unwire = wireEngineEvents(canvas, {
    onResize: () => {
      ({ w, h } = sizeCanvasToParent(canvas, ctx));
    },
    onPointer: (e) => {
      pointer.tx = e.clientX / window.innerWidth;
      pointer.ty = e.clientY / window.innerHeight;
    },
    onVisibility: () => {
      if (document.hidden) loop.stop();
      else if (!reduced) loop.start();
    },
  });

  loop.start();
  return () => {
    loop.stop();
    unwire();
  };
};

export const ParticleField = ({ count = 56, className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    return startParticleEngine(canvas, count);
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 ${className}`}
      aria-hidden="true"
    />
  );
};
